/**
 * masterActsLog — service unique d'archivage des actes de l'équipe master.
 *
 * Toutes les écritures dans la table master_team_acts passent par logAct().
 * Doctrine : l'archivage est best-effort (jamais bloquant pour l'action métier),
 * zéro donnée factice — l'acteur est résolu depuis la session réelle
 * (localStorage rectorium_user, alimenté par useAuth) et, si possible, depuis
 * l'annuaire rectorium_team_members (correspondance email).
 */
import { supabase } from './supabase';

export const ACT_TYPES = [
  'config_change',
  'tenant_action',
  'maintenance',
  'ai_task',
  'invitation',
  'security',
];

/** Libellés FR des types d'actes (journal + filtres). */
export const ACT_TYPE_LABELS = {
  config_change: 'Configuration',
  tenant_action: 'Action tenant',
  maintenance: 'Maintenance',
  ai_task: 'Tâche IA',
  invitation: 'Invitation',
  security: 'Sécurité',
};

/** Acteur courant depuis la session réelle (null si non connecté). */
export function getCurrentActor(storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  try {
    const raw = storage?.getItem('rectorium_user');
    const user = raw ? JSON.parse(raw) : null;
    if (!user) return null;
    return {
      profileId: user.id || null,
      name: user.full_name || user.name || user.email || null,
      email: user.email || null,
    };
  } catch {
    return null;
  }
}

// Cache mémoire : email → id du membre d'équipe (résolu une fois par session).
let memberIdCache = new Map();
/** @internal — réservé aux tests. */
export function __resetMemberIdCache() {
  memberIdCache = new Map();
}

async function resolveMemberId(email, client) {
  if (!email) return null;
  const key = email.toLowerCase();
  if (memberIdCache.has(key)) return memberIdCache.get(key);
  try {
    const { data } = await client
      .from('rectorium_team_members')
      .select('id')
      .ilike('email', key)
      .maybeSingle();
    const id = data?.id || null;
    memberIdCache.set(key, id);
    return id;
  } catch {
    return null;
  }
}

/**
 * Archive un acte de l'équipe master.
 * @param {object} act
 * @param {string} act.type   config_change | tenant_action | maintenance | ai_task | invitation | security
 * @param {string} [act.scope]  domaine touché (ex : tenants, pricing_user_tiers, support_tickets…)
 * @param {string} [act.target] cible précise (slug tenant, code forfait, id ticket…)
 * @param {object} [act.payload] détail sérialisable de l'acte
 * @param {string} [act.result]  résultat (created, updated, resolved, error:…)
 * @param {object} [deps] injection pour les tests ({ client })
 * @returns {Promise<{ok: boolean, error?: any}>} jamais de throw — best-effort.
 */
export async function logAct({ type, scope = null, target = null, payload = {}, result = null } = {}, deps = {}) {
  const client = deps.client || supabase;
  if (!type || !ACT_TYPES.includes(type)) {
    console.warn('[masterActsLog] type d\'acte invalide, acte ignoré:', type);
    return { ok: false, error: new Error(`act_type invalide: ${type}`) };
  }
  const actor = deps.actor !== undefined ? deps.actor : getCurrentActor();
  const actorMemberId = deps.actorMemberId !== undefined
    ? deps.actorMemberId
    : await resolveMemberId(actor?.email, client);
  try {
    const { error } = await client.from('master_team_acts').insert([{
      actor_profile_id: actor?.profileId || null,
      actor_member_id: actorMemberId || null,
      act_type: type,
      scope,
      target,
      payload: {
        ...payload,
        actor_name: actor?.name || null,
        actor_email: actor?.email || null,
      },
      result,
    }]);
    if (error) throw error;
    return { ok: true };
  } catch (error) {
    console.warn('[masterActsLog] archivage impossible (non bloquant):', error?.message || error);
    return { ok: false, error };
  }
}
